import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, PlusCircle, Calendar, MapPin, Sparkles, Trash2, Lightbulb } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import BottomNav from '../components/BottomNav';

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString('es-MX', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

function todayLocalISOInput() {
  const d = new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

export default function OrganizerDashboardPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', event_datetime: todayLocalISOInput(),
    venue_name: '', address: '', city: '', state: '', country: 'MX',
    latitude: '', longitude: '',
  });

  const isOrganizer = useMemo(() => {
    const r = new Set(user?.roles || []);
    return r.has('organizer') || r.has('admin');
  }, [user]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/events/mine/organized');
      setItems(data.items || []);
    } catch (e) {
      if (e?.response?.status === 403) toast.error('Necesitas rol de Organizador');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (isOrganizer) load(); else setLoading(false); }, [isOrganizer]);

  const create = async () => {
    if (!form.title.trim() || !form.address.trim() || !form.city.trim() || !form.country.trim() || !form.event_datetime) {
      toast.error('Completa los campos obligatorios');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        event_datetime: new Date(form.event_datetime).toISOString(),
        venue_name: form.venue_name.trim() || null,
        address: form.address.trim(),
        city: form.city.trim(),
        state: form.state.trim() || null,
        country: form.country.trim(),
        latitude: form.latitude ? Number(form.latitude) : null,
        longitude: form.longitude ? Number(form.longitude) : null,
      };
      const { data } = await api.post('/events', payload);
      toast.success(`Evento #${data.event_number} creado`);
      setShowForm(false);
      setForm({ ...form, title: '', description: '', venue_name: '', address: '', latitude: '', longitude: '' });
      load();
      nav(`/events/${data.event_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo crear el evento');
    } finally { setSaving(false); }
  };

  const cancel = async (event_id) => {
    if (!window.confirm('¿Cancelar este evento?')) return;
    try {
      await api.delete(`/events/${event_id}`);
      toast.success('Evento cancelado');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  if (!isOrganizer) {
    return (
      <div className="min-h-screen bg-aura-bg pb-28 relative">
        <div className="absolute inset-0 bg-aura-radial pointer-events-none" />
        <header className="relative flex items-center justify-between px-5 pt-6">
          <button onClick={() => nav(-1)} className="text-aura-textDim flex items-center gap-1" data-testid="btn-back">
            <ChevronLeft size={20} />
          </button>
          <div className="font-display font-black tracking-[0.3em] aura-text-gradient text-sm">ORGANIZADOR</div>
          <span className="w-8" />
        </header>
        <main className="relative mx-auto max-w-md px-5 pt-10">
          <div className="aura-card p-6 text-center" data-testid="no-role">
            <div className="text-4xl mb-2">🎪</div>
            <div className="font-display font-black text-white text-lg">Solo Organizadores</div>
            <p className="text-sm text-aura-textDim mt-2">
              Solicita el rol de <span className="text-aura-cyan">organizer</span> a un administrador para crear eventos.
            </p>
            <Link to="/events" className="aura-btn-ghost mt-4 inline-flex">Ver calendario</Link>
          </div>
        </main>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-aura-bg pb-28 relative">
      <div className="absolute inset-0 bg-aura-radial pointer-events-none" />
      <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-aura-cyan/20 blur-3xl" />

      <header className="relative flex items-center justify-between px-5 pt-6">
        <button onClick={() => nav(-1)} className="text-aura-textDim flex items-center gap-1" data-testid="btn-back">
          <ChevronLeft size={20} />
        </button>
        <div className="font-display font-black tracking-[0.3em] aura-text-gradient text-sm">MIS EVENTOS</div>
        <span className="w-8" />
      </header>

      <main className="relative mx-auto max-w-md px-5 pt-4 space-y-4">
        <Link
          to="/suggestions"
          data-testid="btn-goto-suggestions"
          className="aura-card p-3 flex items-center gap-3 hover:border-aura-yellow/40 transition"
        >
          <div className="h-9 w-9 rounded-full bg-aura-yellow/15 grid place-items-center">
            <Lightbulb size={16} className="text-aura-yellow" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-black text-white text-sm">Buzón de Sugerencias</div>
            <div className="text-[11px] text-aura-textDim">Propón mejoras al equipo AURA</div>
          </div>
          <ChevronLeft size={16} className="text-aura-textDim rotate-180" />
        </Link>

        {!showForm ? (
          <button
            onClick={() => setShowForm(true)}
            data-testid="btn-open-form"
            className="aura-btn-primary w-full !py-3 justify-center"
          >
            <PlusCircle size={16} /> Nuevo evento
          </button>
        ) : (
          <section className="aura-card p-4 space-y-3" data-testid="event-form">
            <div className="font-display font-black text-white tracking-wider">CREAR EVENTO</div>
            <input data-testid="in-title" placeholder="Título *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full bg-aura-surface2 border border-white/10 rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-aura-cyan/60" />
            <textarea data-testid="in-desc" placeholder="Descripción" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
              className="w-full bg-aura-surface2 border border-white/10 rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-aura-cyan/60" />
            <input data-testid="in-datetime" type="datetime-local" value={form.event_datetime} onChange={(e) => setForm({ ...form, event_datetime: e.target.value })}
              className="w-full bg-aura-surface2 border border-white/10 rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-aura-cyan/60" />
            <input data-testid="in-venue" placeholder="Nombre del venue" value={form.venue_name} onChange={(e) => setForm({ ...form, venue_name: e.target.value })}
              className="w-full bg-aura-surface2 border border-white/10 rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-aura-cyan/60" />
            <input data-testid="in-address" placeholder="Dirección *" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
              className="w-full bg-aura-surface2 border border-white/10 rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-aura-cyan/60" />
            <div className="grid grid-cols-3 gap-2">
              <input data-testid="in-city" placeholder="Ciudad *" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                className="bg-aura-surface2 border border-white/10 rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-aura-cyan/60" />
              <input data-testid="in-state" placeholder="Estado" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })}
                className="bg-aura-surface2 border border-white/10 rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-aura-cyan/60" />
              <input data-testid="in-country" placeholder="País *" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })}
                className="bg-aura-surface2 border border-white/10 rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-aura-cyan/60" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="in-lat" placeholder="Latitud (opcional)" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })}
                className="bg-aura-surface2 border border-white/10 rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-aura-cyan/60" />
              <input data-testid="in-lng" placeholder="Longitud (opcional)" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })}
                className="bg-aura-surface2 border border-white/10 rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-aura-cyan/60" />
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={() => setShowForm(false)} className="aura-btn-ghost flex-1">Cancelar</button>
              <button data-testid="btn-create-event" onClick={create} disabled={saving} className="aura-btn-primary flex-1">
                {saving ? 'Creando…' : 'Crear evento'}
              </button>
            </div>
          </section>
        )}

        {loading ? (
          <div className="text-center py-16 text-aura-textDim animate-aura-pulse text-4xl">⚡</div>
        ) : items.length === 0 ? (
          <div className="aura-card p-8 text-center text-sm text-aura-textDim" data-testid="mine-empty">
            Aún no organizas eventos. Crea el primero arriba.
          </div>
        ) : (
          <ul className="space-y-3" data-testid="mine-list">
            {items.map((e) => (
              <li key={e.event_id} className="aura-card p-4">
                <div className="flex items-start justify-between gap-3">
                  <Link to={`/events/${e.event_id}`} className="min-w-0 flex-1" data-testid={`mine-item-${e.event_number}`}>
                    <div className="text-[10px] font-mono text-aura-cyan tracking-[0.25em]">#EVT-{String(e.event_number).padStart(4, '0')}</div>
                    <div className="font-display font-black text-white text-lg truncate">{e.title}</div>
                    <div className="mt-1 text-xs text-aura-textDim flex items-center gap-1"><Calendar size={12} /> {fmtDate(e.event_datetime)}</div>
                    <div className="mt-0.5 text-xs text-aura-textDim flex items-center gap-1"><MapPin size={12} /> {e.city}</div>
                    <div className="mt-2 flex gap-3 text-[11px] font-mono">
                      <span className="text-aura-cyan">{e.contestants_count} contendientes</span>
                      <span className="text-white/70">{e.battles_count} batallas</span>
                      {e.winners?.length > 0 && <span className="text-aura-gold inline-flex items-center gap-1"><Sparkles size={11} /> {e.winners.length}</span>}
                    </div>
                  </Link>
                  {e.status !== 'cancelled' && e.status !== 'finished' && (
                    <button onClick={() => cancel(e.event_id)} data-testid={`btn-cancel-${e.event_number}`}
                      className="text-red-400 hover:text-red-300 shrink-0 p-2">
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </main>
      <BottomNav />
    </div>
  );
}
